import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Droplets, CheckCircle2, Crown, Users } from "lucide-react";
import { DAYS_FULL, todayISO } from "@/lib/api";
import { toast } from "sonner";

export default function BathSchedule() {
    const { children, activeChildId } = useApp();
    const { isChild, user } = useAuth();
    const [status, setStatus] = useState(null);
    const [history, setHistory] = useState([]);
    const today = todayISO();

    const load = async () => {
        const [s, h] = await Promise.all([
            api.get("/bath/status"),
            api.get("/bath/checkins"),
        ]);
        setStatus(s.data);
        setHistory(h.data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeChildId]);

    const markBath = async (childId) => {
        if (isChild && childId !== user.id) return;
        await api.post("/bath/checkins", { child_id: childId, date: today });
        toast.success("Banho registrado!");
        load();
    };

    const setFirst = async (childId) => {
        if (isChild) return;
        await api.post(`/bath/set-first`, null, { params: { child_id: childId, date: today } });
        toast.success("Ordem atualizada");
        load();
    };

    if (!status) return null;

    const childrenMap = Object.fromEntries(children.map((c) => [c.id, c]));
    const entryByChild = Object.fromEntries((status.today_entries || []).map((e) => [e.child_id, e]));
    const firstId = status.today_first_child_id;
    const expectedId = status.expected_first_child_id;
    const todayHistoryGrouped = history.reduce((acc, h) => {
        if (!acc[h.date]) acc[h.date] = [];
        acc[h.date].push(h);
        return acc;
    }, {});
    const historyDates = Object.keys(todayHistoryGrouped).sort().reverse().slice(0, 7);

    return (
        <div className="space-y-6">
            <div className="rise">
                <div className="text-xs uppercase tracking-widest text-[#8a7968]">
                    {new Date(today + "T00:00").toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" })}
                </div>
                <h1 className="text-3xl sm:text-4xl font-bold mt-1" style={{ fontFamily: "Outfit" }}>
                    Hora do <span className="text-sky-600">banho</span>
                </h1>
                <p className="mt-2 text-slate-600 max-w-xl">
                    Todo dia os dois tomam banho. O app marca <strong>quem foi o primeiro</strong> — e isso define a vez no dia seguinte.
                </p>
            </div>

            <Card className="p-6 border-sky-200 bg-sky-50/40 rise rise-1" data-testid="bath-primeiro-card">
                <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-xl bg-sky-600 text-white grid place-items-center">
                        <Crown className="h-6 w-6" />
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-widest text-sky-800">Primeiro de hoje</div>
                        {firstId ? (
                            <div className="text-xl font-bold text-sky-900" style={{ fontFamily: "Outfit" }}>
                                {childrenMap[firstId]?.name || "—"}
                            </div>
                        ) : expectedId ? (
                            <div className="text-xl font-bold text-sky-900" style={{ fontFamily: "Outfit" }}>
                                Vez de {childrenMap[expectedId]?.name || "—"}
                            </div>
                        ) : (
                            <div className="text-xl font-bold text-sky-900" style={{ fontFamily: "Outfit" }}>
                                Quem for primeiro hoje marca a vez
                            </div>
                        )}
                        <div className="text-xs text-sky-700 mt-0.5">
                            {firstId ? "Já definido no primeiro check-in de hoje" : "Ainda ninguém marcou"}
                        </div>
                    </div>
                </div>
            </Card>

            <div className="grid md:grid-cols-2 gap-4 rise rise-2">
                {children.map((c) => {
                    const entry = entryByChild[c.id];
                    const done = !!entry;
                    const isFirst = entry?.is_first;
                    const isExpectedFirst = expectedId === c.id && !firstId;
                    const canMark = !isChild || c.id === user.id;
                    return (
                        <Card key={c.id} className={`p-5 border ${isFirst ? "border-amber-300 bg-amber-50/50" : "border-[#E2DCD5]"}`} data-testid={`bath-child-${c.id}`}>
                            <div className="flex items-center gap-3">
                                <div className="h-12 w-12 rounded-full grid place-items-center text-white font-bold" style={{ backgroundColor: c.avatar_color || "#D96B43" }}>
                                    {c.name?.[0]?.toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>{c.name}</div>
                                    <div className="text-xs text-slate-500">
                                        {done ? (isFirst ? "Primeiro ★" : "Já tomou") : isExpectedFirst ? "Vez dele(a) ser primeiro" : "Pendente"}
                                    </div>
                                </div>
                                {isFirst && (
                                    <span className="chip bg-amber-100 text-amber-900 border border-amber-200">
                                        <Crown className="h-3 w-3" /> primeiro
                                    </span>
                                )}
                                {done && !isFirst && <CheckCircle2 className="h-5 w-5 text-emerald-600" />}
                            </div>
                            <div className="mt-4 flex flex-wrap gap-2">
                                {!done && canMark && (
                                    <Button
                                        onClick={() => markBath(c.id)}
                                        data-testid={`bath-mark-${c.id}`}
                                        className="bg-sky-600 hover:bg-sky-700 text-white rounded-full"
                                    >
                                        <Droplets className="h-4 w-4 mr-1.5" /> Marcar banho
                                    </Button>
                                )}
                                {!isChild && done && !isFirst && (
                                    <Button
                                        onClick={() => setFirst(c.id)}
                                        data-testid={`bath-set-first-${c.id}`}
                                        variant="outline"
                                        className="rounded-full"
                                    >
                                        <Crown className="h-4 w-4 mr-1.5" /> Marcar como primeiro
                                    </Button>
                                )}
                                {!isChild && !done && (
                                    <Button
                                        onClick={() => setFirst(c.id)}
                                        data-testid={`bath-set-first-${c.id}`}
                                        variant="outline"
                                        className="rounded-full"
                                    >
                                        <Crown className="h-4 w-4 mr-1.5" /> Fazer dele(a) o primeiro
                                    </Button>
                                )}
                            </div>
                        </Card>
                    );
                })}
            </div>

            <Card className="p-6 border-[#E2DCD5] rise rise-3">
                <div className="flex items-center gap-2 mb-3">
                    <Users className="h-5 w-5 text-sky-600" />
                    <h2 className="font-semibold" style={{ fontFamily: "Outfit" }}>Últimos 7 dias — quem foi primeiro</h2>
                </div>
                {historyDates.length === 0 ? (
                    <p className="text-sm text-slate-500">Nenhum registro ainda.</p>
                ) : (
                    <ul className="divide-y divide-[#E2DCD5]">
                        {historyDates.map((d) => {
                            const entries = todayHistoryGrouped[d];
                            const first = entries.find((e) => e.is_first);
                            const firstChild = first ? childrenMap[first.child_id] : null;
                            return (
                                <li key={d} className="flex items-center justify-between py-2.5 text-sm" data-testid={`bath-history-${d}`}>
                                    <span className="text-slate-700">
                                        {new Date(d + "T00:00").toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "short" })}
                                    </span>
                                    <div className="flex items-center gap-2">
                                        {firstChild && (
                                            <>
                                                <span className="chip bg-amber-100 text-amber-900"><Crown className="h-3 w-3" /> {firstChild.name}</span>
                                                <span className="text-xs text-slate-500">{entries.length} banho(s)</span>
                                            </>
                                        )}
                                        {!firstChild && <span className="text-xs text-slate-400">—</span>}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
                {!isChild && (
                    <p className="mt-3 text-xs text-slate-500">
                        Dica: a vez do próximo dia é sempre do outro filho do dia anterior. Precisou mudar? Use "Marcar como primeiro" no card acima.
                    </p>
                )}
            </Card>
        </div>
    );
}
